import { BotType, WorkspaceType } from "@shared/types";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import {
  useSelectUser,
  useSubUserPermissions,
} from "@shared/utils/hooks/misc.ts";
import {
  BASE_TRADES_PATH,
  BOTS_PATH,
  CREATE_TRADE_PATH,
  PORTFOLIO_PATH,
  SIGNALS_BOT_PATH,
} from "common/constants.ts";
import React, { useMemo } from "react";
import { AppScreen } from "common/Home/General/types.ts";
import { DashboardIcon } from "common/Icons/Icons.tsx";
import PieChartOutlined from "@ant-design/icons/PieChartOutlined";
import SwapOutlined from "@ant-design/icons/SwapOutlined";
import { ArrowLeftRight, Bot, ChartPie } from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";
import { useSelector } from "react-redux";
import {
  ADMIN_GROUP_MANAGEMENT_MENU_ITEM,
  ADMIN_SIGNALS_MENU_ITEM,
  ADMIN_TRADING_CONFIGURATION_MENU_ITEM,
  AFFILIATION_MENU_ITEM,
  DASHBOARD_MENU_ITEM,
  getBotItemKey,
  MARKETPLACE_MENU_ITEM,
  MY_GROUPS_MENU_ITEM,
  SIGNALS_TERMINAL_MENU_ITEM,
} from "common/Home/Sider/MenuItems.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";

const useBottomNavigatorMenuItems = () => {
  const workspace = useSelector(selectWorkspace);
  const { isReadOnly, isReadWrite } = useSubUserPermissions();
  const user = useSelectUser();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { allowViewOnlyUsersToViewBots } = useBranding();

  const menuItems = useMemo(() => {
    if (!isBottomNavigator) return [];
    if (workspace === WorkspaceType.ADMIN) {
      return user?.isWhopExperience()
        ? LIMITED_WHOP_ADMIN_MENU_ITEMS
        : ADMIN_MENU_ITEMS;
    }
    if (isReadOnly) {
      return allowViewOnlyUsersToViewBots
        ? READ_ONLY_WITH_BOTS_MENU_ITEMS
        : READ_ONLY_MENU_ITEMS;
    }
    let menuItems = USER_MENU_ITEMS;
    if (user?.isWhopExperience()) {
      menuItems = menuItems.map((menuItem) => {
        if (menuItem.key === AppScreen.BOTS) {
          return { ...menuItem, link: SIGNALS_BOT_PATH };
        }
        return menuItem;
      });
    }
    const filterMarketplace = isReadWrite || user?.isWhopExperience();
    if (filterMarketplace) {
      return menuItems.filter(
        (menuItem) => menuItem.key !== AppScreen.MARKETPLACE
      );
    }
    return menuItems;
  }, [workspace, user, isReadOnly, isReadWrite, allowViewOnlyUsersToViewBots]);

  const ignoreBottomNavigatorKeys = useMemo(() => {
    const ignoreKeys = menuItems.map((item) => item.key);
    return ignoreKeys.reduce((acc, key) => {
      const relatedKeys = ITEM_KEY_TO_RELATED_KEYS[key];
      if (relatedKeys?.length) acc.push(...relatedKeys);
      return acc;
    }, ignoreKeys);
  }, [menuItems, isBottomNavigator]);

  return {
    menuItems,
    ignoreBottomNavigatorKeys,
  };
};

export default useBottomNavigatorMenuItems;

const ITEM_KEY_TO_RELATED_KEYS = {
  [AppScreen.PORTFOLIO]: [AppScreen.ACCOUNTS, AppScreen.ASSETS],
  [AppScreen.BOTS]: Object.values(BotType)
    .filter((botType) => botType !== BotType.TRADING_VIEW_SIGNALS)
    .map((botType) => getBotItemKey(botType as BotType)),
};

const BOTS_MENU_ITEM = {
  key: AppScreen.BOTS,
  name: "page_title.bots.bots",
  icon: (
    <LookIcon
      classic={<DashboardIcon />}
      newLook={<Bot strokeWidth={1.75} />}
    />
  ),
  link: BOTS_PATH,
  routerMatch: BOTS_PATH,
};
const PORTFOLIO_MENU_ITEM = {
  key: AppScreen.PORTFOLIO,
  name: "page_title.portfolio",
  icon: (
    <LookIcon
      classic={<PieChartOutlined />}
      newLook={<ChartPie strokeWidth={1.75} />}
    />
  ),
  link: PORTFOLIO_PATH,
  routerMatch: PORTFOLIO_PATH,
};
const TRADES_MENU_ITEM = {
  key: AppScreen.TRADES,
  name: "page_title.trades",
  icon: (
    <LookIcon
      classic={<SwapOutlined />}
      newLook={<ArrowLeftRight strokeWidth={1.75} />}
    />
  ),
  link: BASE_TRADES_PATH,
  routerMatch: [BASE_TRADES_PATH, CREATE_TRADE_PATH],
};
const USER_MENU_ITEMS = [
  DASHBOARD_MENU_ITEM,
  PORTFOLIO_MENU_ITEM,
  MARKETPLACE_MENU_ITEM,
  BOTS_MENU_ITEM,
  TRADES_MENU_ITEM,
];
const READ_ONLY_MENU_ITEMS = [
  DASHBOARD_MENU_ITEM,
  PORTFOLIO_MENU_ITEM,
  TRADES_MENU_ITEM,
];
const READ_ONLY_WITH_BOTS_MENU_ITEMS = [
  ...READ_ONLY_MENU_ITEMS.slice(0, 2),
  BOTS_MENU_ITEM,
  ...READ_ONLY_MENU_ITEMS.slice(2),
];
const LIMITED_WHOP_ADMIN_MENU_ITEMS = [
  {
    ...ADMIN_TRADING_CONFIGURATION_MENU_ITEM,
    name: "page_title.configuration",
  },
  ADMIN_SIGNALS_MENU_ITEM,
  SIGNALS_TERMINAL_MENU_ITEM,
  MY_GROUPS_MENU_ITEM,
];
const ADMIN_MENU_ITEMS = [
  { ...ADMIN_GROUP_MANAGEMENT_MENU_ITEM, name: "page_title.users" },
  {
    ...ADMIN_TRADING_CONFIGURATION_MENU_ITEM,
    name: "page_title.configuration",
  },
  ADMIN_SIGNALS_MENU_ITEM,
  AFFILIATION_MENU_ITEM,
];
